import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { ApiError, api } from '@/lib/api';

/**
 * Матрица «ученик × предмет» с галочками — результаты выбора по одному
 * периоду. + кнопка экспорта в Excel.
 */
function fmtDateRu(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  return iso.replace('T', ' ').slice(0, 16);
}

export default function ExtracurricularSelectionResultsPage() {
  const { tweaks, updateTweak } = useShell();
  const navigate = useNavigate();
  const { id } = useParams();
  const periodId = parseInt(id, 10);

  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError]  = useState(null);

  useEffect(() => {
    if (!periodId) return;
    setLoad(true); setError(null);
    api.get(`/api/extracurricular-selection-periods/${periodId}/results`)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  }, [periodId]);

  const period   = data?.period;
  const subjects = data?.subjects || [];
  const rows     = data?.rows || [];

  // Считаем по столбцам — сколько детей выбрали каждый предмет.
  const colCounts = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      for (const sid of r.subjectIds) {
        map.set(sid, (map.get(sid) || 0) + 1);
      }
    }
    return map;
  }, [rows]);

  const onDeleteRow = async (row) => {
    if (!confirm(`Удалить заявку «${row.studentName}»? Это сотрёт его выбор по этому периоду — пользователь сможет подать заявку заново.`)) return;
    try {
      await api.delete('/api/extracurricular-selection-results/' + row.resultId);
      setData((d) => d ? { ...d, rows: d.rows.filter((r) => r.resultId !== row.resultId) } : d);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  return (
    <>
      <Topbar
        breadcrumb={[
          { label: 'Внеурочные занятия', to: '/extracurricular/selections' },
          { label: 'Выбор занятий', to: '/extracurricular/selections' },
          period
            ? `Период ${fmtDateRu(period.dateStartPeriod)}–${fmtDateRu(period.dateEnd)}`
            : 'Результаты',
        ]}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="filterbar">
        <div className="filterbar__row" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn--sm" onClick={() => navigate('/extracurricular/selections')}>
            ‹ К списку
          </button>
          {period && (
            <span style={{ fontSize: 13 }}>
              <b>Период:</b> {fmtDateRu(period.dateStartPeriod)} – {fmtDateRu(period.dateEnd)}
              {period.selectionMin > 0 || period.selectionMax > 0
                ? <> · от {period.selectionMin || '0'} до {period.selectionMax || '∞'} занятий</>
                : null}
              {' · '}
              <span className="muted">сбор заявок с {fmtDateRu(period.dateStartSelection)}</span>
              {' · '}
              <span className="muted">заявок: <b style={{ color: 'var(--fg)' }}>{rows.length}</b></span>
            </span>
          )}
          <div style={{ marginLeft: 'auto' }}>
            {periodId && rows.length > 0 && (
              <a href={`/api/extracurricular-selection-periods/${periodId}/results/export`}
                 className="btn btn--primary btn--sm" target="_blank" rel="noopener">
                <Icon name="download" size={12} /> Экспорт в Excel
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {loading && <div className="muted" style={{ padding: 16 }}>Загрузка…</div>}

        {!loading && data && rows.length === 0 && (
          <div className="ref-empty" style={{ padding: 32 }}>
            По этому периоду заявок ещё не подали.
          </div>
        )}

        {!loading && data && rows.length > 0 && (
          <div className="table-wrap" style={{ overflow: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={thAction} aria-label="Удалить" />
                  <th style={{ ...thBase, position: 'sticky', left: 28, zIndex: 3, minWidth: 220 }}>
                    Ученик
                  </th>
                  {subjects.map((s) => (
                    <th key={s.id} style={thRot} title={s.name + (s.archive ? ' (в архиве)' : '')}>
                      {/* Вертикальный заголовок: writing-mode + rotate, читается
                          снизу вверх. Это компактнее, чем диагональ -45°. */}
                      <div style={thRotInner}>
                        <span>{s.name}</span>
                        {colCounts.get(s.id) > 0 && (
                          <span className="muted" style={{ marginLeft: 4, fontSize: 10 }}>
                            ({colCounts.get(s.id)})
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const set = new Set(r.subjectIds);
                  const stripe = i % 2 === 1 ? 'var(--panel-2, rgba(0,0,0,0.025))' : 'var(--panel)';
                  return (
                    <tr key={r.resultId}>
                      <td style={{ ...tdAction, background: stripe }}>
                        <button type="button"
                                onClick={() => onDeleteRow(r)}
                                title={'Удалить заявку: ' + r.studentName}
                                style={{
                                  border: 'none', background: 'transparent', cursor: 'pointer',
                                  width: 22, height: 22, padding: 0,
                                  color: 'var(--red-fg, #dc2626)', fontSize: 13, lineHeight: 1,
                                }}>
                          ✕
                        </button>
                      </td>
                      <td style={{
                        ...tdBase,
                        position: 'sticky', left: 28, background: stripe,
                        fontWeight: 500, zIndex: 1,
                        whiteSpace: 'nowrap',
                      }}
                          title={'Подано: ' + fmtDateTime(r.time)}>
                        {r.studentName}
                        {r.studentStatus === 'archive' && (
                          <span className="pay-st pay-st--mute" style={{ marginLeft: 6, fontSize: 10 }}>архив</span>
                        )}
                      </td>
                      {subjects.map((s) => (
                        <td key={s.id}
                            style={{
                              ...tdBase,
                              textAlign: 'center',
                              background: set.has(s.id) ? 'rgba(16, 185, 129, 0.18)' : stripe,
                              color: set.has(s.id) ? '#059669' : 'transparent',
                              fontWeight: 700,
                            }}>
                          {set.has(s.id) ? '✓' : ''}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

const thBase = {
  borderBottom: '1px solid var(--border)',
  borderRight:  '1px solid var(--border)',
  padding: '6px 8px',
  fontWeight: 600,
  background: 'var(--panel)',
  textAlign: 'left',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};
const thAction = {
  ...thBase,
  width: 28,
  minWidth: 28,
  padding: 0,
  position: 'sticky',
  left: 0,
  zIndex: 4,
};
const thRot = {
  ...thBase,
  width: 28,
  minWidth: 28,
  maxWidth: 28,
  height: 160,
  verticalAlign: 'bottom',
  textAlign: 'center',
  padding: 4,
};
// writing-mode: vertical-rl + transform(180) → текст читается снизу вверх,
// колонка получается узкая (≈28px), даже если предметов много, всё помещается
// на привычном экране без горизонтального скролла.
const thRotInner = {
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  whiteSpace: 'nowrap',
  display: 'inline-block',
  lineHeight: 1.1,
  fontSize: 11.5,
  fontWeight: 500,
};
const tdBase = {
  borderBottom: '1px solid var(--border)',
  borderRight:  '1px solid var(--border)',
  padding: '3px 6px',
};
const tdAction = {
  borderBottom: '1px solid var(--border)',
  borderRight:  '1px solid var(--border)',
  padding: 0,
  textAlign: 'center',
  position: 'sticky',
  left: 0,
  zIndex: 1,
};
